import * as React from "react";
import { Box, Card, Chip, Link, Stack, Tooltip } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { ArrowLeftIcon, TrashIcon } from "@phosphor-icons/react";
import { PencilSimpleIcon } from "@phosphor-icons/react/dist/ssr/PencilSimple";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";

import { Field, fieldsKeys, useDeleteField } from "@/hooks/api/use-fields";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useDialog } from "@/hooks/use-dialog";
import { Breadcrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { RouterLink } from "@/components/core/link";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column } from "@/components/customTable/types";
import { DeleteModal } from "@/components/deleteModal/delete-modal";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { CreateField } from "./create";

export function Fields(): React.JSX.Element {
	const [isCreateFieldDrawerOpen, setIsCreateFieldDrawerOpen] = React.useState(false);
	const [selectedFieldToCreate, setSelectedFieldToCreate] = React.useState<Field | null>(null);

	const {
		items: fields,
		isLoading,
		totalItems,
		refetchData: fetchFields,
	} = useCachedTableStateWithUrl({
		queryKey: fieldsKeys.lists(),
		endpoint: "/crm/fields",
	});

	const deleteFieldMutation = useDeleteField();

	const {
		data: selectedFieldToDelete,
		open: isDeleteFieldModalOpen,
		handleClose: handleDeleteFieldModalClose,
		handleOpen: handleDeleteFieldModalOpen,
	} = useDialog<Field | null>();

	const handleDeleteField = async () => {
		if (selectedFieldToDelete?.id) {
			await deleteFieldMutation.mutateAsync(selectedFieldToDelete.id);
			handleDeleteFieldModalClose();
		}
	};

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: {
					id: "id",
					label: "ID",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "id",
			},
			{
				header: {
					id: "name",
					label: "Name",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "name",
			},
			{
				header: {
					id: "type",
					label: "Type",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "type",
			},
			{
				header: {
					id: "required",
					label: "Required",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "required",
				getCellValue: (row: any) => {
					const isRequired = row.required;

					if (!isRequired) {
						return null;
					}

					return (
						<Chip
							// icon={<CheckCircleIcon color="var(--mui-palette-success-main)" weight="fill" />}
							label={"Required"}
							size="small"
							variant="filled"
							color="success"
						/>
					);
				},
			},
			{
				header: {
					id: "label",
					label: "Label",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "label",
			},
			{
				header: {
					id: "description",
					label: "Description",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "description",
			},
			{
				header: {
					id: "options",
					label: "Options",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "options",
				getCellValue: (row: any) => {
					if (Object?.keys(row?.options)?.length === 0) {
						return null;
					}

					return (
						<Tooltip title={JSON.stringify(row?.options, null, 2)}>
							<Chip label="Options" color="primary" variant="outlined" size="small" sx={{ cursor: "pointer" }}></Chip>
						</Tooltip>
					);
				},
			},

			{
				header: {
					id: "actions",
					label: "",
					width: 100,
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "actions",
				getCellValue: (row: any) => (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
						<TableActions
							actions={[
								{
									label: "Edit",
									icon: <PencilSimpleIcon width={20} height={20} weight="fill" />,
									onClick: () => {
										setSelectedFieldToCreate(row);
										setIsCreateFieldDrawerOpen(true);
									},
								},
								{
									label: "Delete",
									icon: <TrashIcon width={20} height={20} weight="fill" />,
									sx: { color: "error.main" },
									onClick: () => handleDeleteFieldModalOpen(row),
								},
							]}
						/>
					</Box>
				),
			},
		];
	}, []);

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack direction={"column"} spacing={3}>
						<Breadcrumbs>
							<Link
								color="text.primary"
								component={RouterLink}
								href={"/contacts"}
								sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
								variant="subtitle2"
							>
								<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
								Contacts
							</Link>

							<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
								/
							</Typography>
							<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
								Fields
							</Typography>
						</Breadcrumbs>

						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={3}
							sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
						>
							<Box sx={{ flex: "1 1 auto" }}>
								<Typography variant="h4">Fields Management</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
									Manage fields and their details
								</Typography>
							</Box>
							<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
								<Button
									startIcon={<PlusIcon />}
									variant="contained"
									onClick={() => {
										setIsCreateFieldDrawerOpen(true);
										setSelectedFieldToCreate(null);
									}}
								>
									Add Field
								</Button>
							</Box>
						</Stack>
					</Stack>
					<TableAnimation delay={0}>
						<Card>
							<CustomTable
								columns={columns}
								data={fields}
								refetchData={fetchFields}
								isLoading={isLoading}
								totalItems={totalItems}
								withoutFetchPagination
							/>
						</Card>
					</TableAnimation>
				</Stack>
			</Box>

			<DeleteModal
				open={isDeleteFieldModalOpen}
				onClose={handleDeleteFieldModalClose}
				onDelete={handleDeleteField}
				title="Delete Field"
				description="Are you sure you want to delete this field?"
			/>

			<CreateField
				isCreateFieldDrawerOpen={isCreateFieldDrawerOpen}
				onCreateDrawerClose={() => {
					setIsCreateFieldDrawerOpen(false);

					setTimeout(() => {
						setSelectedFieldToCreate(null);
					}, 300);
				}}
				fieldId={selectedFieldToCreate?.id || null}
			/>
		</React.Fragment>
	);
}
